<template>
  <BaseCard type="filled" class="relative flex flex-col p-3">
    <div class="flex flex-row items-center">
      <BaseMemberAvatar :member="receiver" size="56" class="mr-4 ml-1" />
      <div class="flex min-w-0 shrink grow flex-col">
        <p class="text-body-medium text-on-surface-variant mb-0.5">
          Angefangenes Feedback
          <span class="lowercase">{{ $t('common.to') }}</span>
          {{ memberAbbreviatedName(receiver) }} · <TimeAgo :date="task.createdAt" />
        </p>
        <h3 class="text-title-large">{{ task.title ?? feedbackTopicTitles }}</h3>
        <div class="mt-1.5 flex flex-row items-center">
          <BaseCircularProgressIndictator
            class="mr-1 shrink-0"
            :percentage="(numberOfGivenFeedbacks / task.numberOfQuestions) * 100"
            :radius="10"
            :thickness="4"
          />
          <span
            class="text-body-medium text-on-surface-variant overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {{ task.numberOfQuestions }} {{ $t('common.question', task.numberOfQuestions) }}
            <span v-if="task.title"> · {{ feedbackTopicTitles }}</span></span
          >
        </div>
      </div>
      <BaseButtonGroup class="self-center">
        <BaseMenu :items="menuItems" v-slot="{ open }">
          <BaseIconButton icon="more_vert" size="M" width="narrow" :force-pressed="open" />
        </BaseMenu>
        <BaseTextButton size="M" color="tonalPrimary" @click="routeToTask" width="wide">{{
          feedbacks.length > 0 ? $t('common.continue') : $t('common.start')
        }}</BaseTextButton>
      </BaseButtonGroup>
    </div>
    <BaseDialog
      type="basic"
      :is-open="isOpen"
      @close="closeDialog"
      title="Selbsteinschätzung löschen"
      description="Möchtest du die Selbsteinschätzung wirklich löschen?"
    >
      <template #confirm
        ><BaseTextButton @click="deleteSurvey">Feedback löschen</BaseTextButton></template
      >
      <template #dismiss
        ><BaseTextButton @click="closeDialog">{{ $t('common.cancel') }}</BaseTextButton></template
      >
    </BaseDialog>
  </BaseCard>
</template>

<script setup lang="ts">
  import type { FeedbackTask } from '@/modules/task/composables/useFeedbackTasks'
  import { computed, ref } from 'vue'
  import BaseCard from '../base/card/BaseCard.vue'
  import { OpenFeedbackState } from '@/modules/open-feedbacks/composables/useOpenFeedbacks'
  import TimeAgo from '../base/time-ago/TimeAgo.vue'
  import { useRouter } from 'vue-router'
  import { useLocalFeedbacks } from '@/core/composables/useLocalFeedbacks'
  import BaseCircularProgressIndictator from '../base/circlular-progress-indicator/BaseCircularProgressIndictator.vue'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import BaseIconButton from '../base/button/icon-button/BaseIconButton.vue'
  import { useI18n } from 'vue-i18n'
  import BaseMenu from '../base/menu/BaseMenu.vue'
  import BaseButtonGroup from '../base/button/BaseButtonGroup.vue'
  import BaseDialog from '../base/dialog/BaseDialog.vue'
  import { useDialog } from '../base/dialog/use-dialog'
  import type { SingleMenuItem } from '../base/menu/MenuItem'
  import useDelete from '@/core/composables/api/useDelete'
  import { ApiRequestStatus } from '@/core/models/api/api-request-status'
  import { useSnackStore } from '@/stores/snack.store'
  import { EventKey } from '@/core/events/event-key'
  import { useEventBus } from '@vueuse/core'
  import BaseMemberAvatar from '../base/avatar/BaseMemberAvatar.vue'
  import { memberAbbreviatedName } from '@/core/models/member/member-name'

  const props = defineProps<{
    task: FeedbackTask
  }>()

  const feedbackTopicTitles = computed(() => {
    return props.task.feedbackTopics.map((topic) => topic.title).join(', ')
  })

  const isDone = ref(
    props.task.surveyTandems.every((tandem) => tandem.senderState === OpenFeedbackState.Completed),
  )

  const { feedbacksBySurveyIdAndReceiverId } = useLocalFeedbacks()

  const receiver = computed(() => {
    return props.task.surveyTandems[0].receiver
  })

  const feedbacks = feedbacksBySurveyIdAndReceiverId(props.task.surveyId, receiver.value.id)

  const numberOfGivenFeedbacks = computed(() => {
    return feedbacks.value.length
  })

  const { isOpen, openDialog, closeDialog } = useDialog()

  const { t } = useI18n()

  const { remove: removeSurvey, status: statusDeleteSurvey } = useDelete<string>(
    `survey/${props.task.id}`,
  )

  const snackStore = useSnackStore()
  const bus = useEventBus(EventKey.openFeedbackUpdated)

  const deleteSurvey = async () => {
    await removeSurvey({})

    switch (statusDeleteSurvey.value) {
      case ApiRequestStatus.Success:
        snackStore.showInfo(t('survey.delete.success'))
        closeDialog()
        bus.emit({ surveyId: props.task.surveyId })
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('survey.delete.error'))
        break
    }
  }

  const menuItems = computed(() => {
    const items: SingleMenuItem[] = []
    items.push({
      id: 'delete',
      title: t('common.delete'),
      leadingIcon: 'delete',
      destructive: true,
      callback: () => {
        openDialog()
      },
    })

    return items
  })

  const router = useRouter()

  const routeToTask = () => {
    if (isDone.value) return
    router.push({
      name: 'feedback',
      params: { surveyId: props.task.surveyId, receiverId: receiver.value.id },
    })
  }
</script>
